import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Fach, Katalog, Schrank } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import { beschreibe, fachName } from '../komponenten/beschreibung'
import ErgebnisFormular, { ausErgebnis, type ErgebnisWerte } from '../komponenten/ErgebnisFormular'
import Konfidenz from '../komponenten/Konfidenz'

/**
 * Schritt 5: Vorschlag und Bestaetigung je Fach. Der Grundsatz des Piloten steht hier im Code:
 * das System schlaegt vor, der Mensch bestaetigt per Tipp. Es gibt keine Pflichteingabe ohne
 * Vorbelegung (P5-01 bis P5-04).
 */
export default function FachBestaetigen() {
  const { fachId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()
  const [werte, setWerte] = useState<ErgebnisWerte | null>(null)
  /** Sobald der Anwender etwas angetippt hat, wird nicht mehr nachbelegt. */
  const [beruehrt, setBeruehrt] = useState(false)

  const fach = useQuery({
    queryKey: ['fach', fachId],
    queryFn: () => api.get<Fach>(`/api/faecher/${fachId}`),
  })
  const katalog = useQuery({ queryKey: ['katalog'], queryFn: () => api.get<Katalog>('/api/katalog') })

  const schrank = useQuery({
    queryKey: ['schrank', fach.data?.schrankId],
    queryFn: () => api.get<Schrank>(`/api/schraenke/${fach.data!.schrankId}`),
    enabled: Boolean(fach.data?.schrankId),
  })

  // Vorbelegung: bereits bestaetigtes Ergebnis, sonst der Vorschlag des Systems.
  //
  // Nachbelegt wird bei jedem neuen Serverstand, solange der Anwender nichts angetippt hat. Wurde
  // nur einmal belegt, blieb ein zwischengespeicherter Stand stehen: TanStack Query liefert beim
  // Betreten zuerst die alten Daten und holt erst danach nach. Nach dem Zuruecksetzen und einer
  // neuen Aufnahme desselben Fachs stand so das verworfene Ergebnis im Formular, waehrend oben
  // bereits der neue Vorschlag zu sehen war.
  useEffect(() => {
    if (!fach.data || beruehrt) return
    const grundlage = fach.data.nutzerErgebnis ?? fach.data.kiVorschlag
    setWerte(ausErgebnis(grundlage))
  }, [fach.data, beruehrt])

  const bestaetigen = useMutation({
    mutationFn: () => api.post<Fach>(`/api/faecher/${fachId}/bestaetigen`, werte),
    onSuccess: (bestaetigt) => {
      // Nach dem Bestaetigen geht es ohne Umweg mit dem naechsten Fach weiter, solange
      // die Sollzahl des Schranks noch nicht erreicht ist.
      const naechste = bestaetigt.fachNr + 1
      const soll = schrank.data?.anzahlFaecherSoll ?? 0
      if (naechste <= soll) {
        navigiere(`/schraenke/${bestaetigt.schrankId}/aufnahme/${naechste}`)
      } else {
        navigiere(`/schraenke/${bestaetigt.schrankId}`)
      }
    },
  })

  const zuruecksetzen = useMutation({
    mutationFn: () => api.post<Fach>(`/api/faecher/${fachId}/zuruecksetzen`),
    onSuccess: (zurueckgesetzt) => {
      // Der zwischengespeicherte Stand gehoert zur verworfenen Aufnahme und darf nicht wieder
      // auftauchen - entfernen statt nur fuer veraltet erklaeren.
      abfragen.removeQueries({ queryKey: ['fach', fachId] })
      abfragen.invalidateQueries({ queryKey: ['schrank', zurueckgesetzt.schrankId] })
      abfragen.invalidateQueries({ queryKey: ['vollstaendigkeit', String(zurueckgesetzt.schrankId)] })
      navigiere(`/schraenke/${zurueckgesetzt.schrankId}/aufnahme/${zurueckgesetzt.fachNr}`)
    },
  })

  const loeschen = useMutation({
    mutationFn: () => api.loeschen(`/api/faecher/${fachId}`),
    onSuccess: () => navigiere(`/schraenke/${fach.data?.schrankId ?? ''}`),
  })

  const vorschlag = fach.data?.kiVorschlag ?? null

  return (
    <AppRahmen
      titel={schrank.data?.bezeichnung ?? 'Fach'}
      unter={fach.data?.fachCode}
      zurueck={fach.data ? `/schraenke/${fach.data.schrankId}` : '/'}
      pfad={fach.data?.pfad}
      ebene="fach"
      pfadZusatz={fach.data ? fachName(fach.data) : undefined}
      menue={[
        {
          beschriftung: 'Zur Fachübersicht',
          beiKlick: () => fach.data && navigiere(`/schraenke/${fach.data.schrankId}`),
        },
        {
          beschriftung: 'Fach umbenennen',
          eingabe: {
            feld: 'Bezeichnung',
            wert: fach.data?.bezeichnung ?? '',
            hinweis: `Etwa „oben links“. Die Nummer ${fach.data?.fachNr ?? ''} und die Kennung ${
              fach.data?.fachCode ?? ''
            } bleiben; leer lassen, um die Bezeichnung zu entfernen.`,
            leerErlaubt: true,
            speichern: async (bezeichnung) => {
              const neu = await api.patch<Fach>(`/api/faecher/${fachId}`, { bezeichnung })
              abfragen.setQueryData(['fach', fachId], neu)
              // Die Bezeichnung steht auch in Fachliste, Report und Büroansicht.
              void abfragen.invalidateQueries()
            },
          },
        },
        {
          beschriftung: 'Fach zurücksetzen',
          gefahr: true,
          bestaetigung: {
            frage:
              'Fotos, Vorschlag und bestätigtes Ergebnis werden verworfen. Das Fach bleibt mit seiner Nummer bestehen und kann neu aufgenommen werden.',
            bestaetigungstext: 'Zurücksetzen und neu aufnehmen',
          },
          beiKlick: () => zuruecksetzen.mutate(),
        },
        {
          beschriftung: 'Fach löschen',
          gefahr: true,
          bestaetigung: {
            frage: 'Das Fach wird samt Fotos entfernt. Die Nummern der übrigen Fächer bleiben unverändert.',
            bestaetigungstext: 'Endgültig löschen',
          },
          beiKlick: () => loeschen.mutate(),
        },
      ]}
      fuss={
        <button
          className="btn btn-primaer"
          disabled={!werte || bestaetigen.isPending}
          onClick={() => bestaetigen.mutate()}
        >
          Bestätigen und weiter
        </button>
      }
    >
      {fach.data && (
        <div className="paar">
          <figure>
            <img src={`/api/faecher/${fach.data.id}/bild/oben`} alt="Draufsicht" />
            <figcaption>Draufsicht</figcaption>
          </figure>
          <figure>
            <img src={`/api/faecher/${fach.data.id}/bild/vorne`} alt="Frontalsicht" />
            <figcaption>Frontalsicht</figcaption>
          </figure>
        </div>
      )}

      {/* Ein bereits bestaetigtes Fach zeigt unten das gespeicherte Ergebnis, oben aber weiterhin
          den urspruenglichen Vorschlag. Ohne diesen Hinweis liest sich der Unterschied wie ein
          Fehler der Vorbelegung. */}
      {fach.data?.nutzerErgebnis && (
        <p className="hinweiszeile">
          Bereits bestätigt von {fach.data.nutzerErgebnis.bestaetigtVon} am{' '}
          {new Date(fach.data.nutzerErgebnis.bestaetigtAm ?? '').toLocaleString('de-DE')}. Unten steht das
          gespeicherte Ergebnis, nicht der Vorschlag.
        </p>
      )}

      {vorschlag && (
        <div className="gruppe">
          <div className="kopfzeile">
            <span className="label">Vorschlag des Systems</span>
            <Konfidenz wert={vorschlag.konfidenz} />
          </div>
          <p className="sek">{beschreibe(vorschlag)}</p>
          {vorschlag.hinweise.map((hinweis) => (
            <p key={hinweis} className="hinweiszeile">
              {hinweis}
            </p>
          ))}
        </div>
      )}

      {werte && katalog.data && (
        <ErgebnisFormular
          werte={werte}
          aendern={(neu) => {
            setBeruehrt(true)
            setWerte(neu)
          }}
          katalog={katalog.data}
          vorschlag={vorschlag ? ausErgebnis(vorschlag) : null}
        />
      )}

      {bestaetigen.isError && <p className="hinweiszeile">{(bestaetigen.error as Error).message}</p>}
    </AppRahmen>
  )
}
